import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

export interface Organization {
  id: string;
  name: string;
  created_at?: string;
  updated_at?: string;
  settings?: any;
}

export interface OrganizationMember {
  id: string;
  user_id: string;
  organization_id: string;
  role: string;
  created_at?: string;
  joined_at?: string; // Mantém para compatibilidade
  organization?: Organization;
}

export interface OrganizationContextType {
  organizationId: string | null;
  organization: Organization | null;
  organizations: Organization[]; // Lista de organizações do usuário
  currentMembership: OrganizationMember | null;
  loading: boolean;
  error: string | null;
  setOrganizationId: (id: string | null) => void;
  switchOrganization: (id: string) => Promise<void>;
  refreshOrganizations: () => Promise<void>;
  clearError: () => void;
  forceRefresh: () => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  
  const [organizationId, setOrganizationIdState] = useState<string | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentMembership, setCurrentMembership] = useState<OrganizationMember | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Buscar organizações do usuário
  const fetchUserOrganizations = useCallback(async () => {
    if (!user?.id) {
      console.log('👤 Nenhum usuário autenticado');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('🏢 Buscando organizações do usuário:', user.id);
      
      // Buscar todas as organizações do usuário
      const { data: memberships, error: membershipsError } = await supabase
        .from('organization_members')
        .select(`
          id,
          user_id,
          organization_id,
          role,
          created_at,
          organization:organizations (
            id,
            name,
            created_at,
            updated_at
          )
        `)
        .eq('user_id', user.id);

      if (membershipsError) {
        console.error('❌ Erro ao buscar organizações:', membershipsError);
        throw membershipsError;
      }

      if (!memberships || memberships.length === 0) {
        console.warn('⚠️ Usuário sem organização - criando organização pessoal');
        
        try {
          // Criar organização pessoal para o usuário
          const { data: newOrg, error: createError } = await supabase
            .from('organizations')
            .insert({
              name: `Organização de ${user.email?.split('@')[0] || 'Usuário'}`,
              created_by: user.id
            })
            .select()
            .single();
          
          if (createError || !newOrg) {
            // Se não conseguir criar, tenta buscar uma organização padrão existente
            const { data: defaultOrg } = await supabase
              .from('organizations')
              .select('*')
              .eq('name', 'Organização Padrão')
              .single();
            
            if (defaultOrg) {
              // Adicionar usuário à organização padrão
              await supabase
                .from('organization_members')
                .insert({
                  user_id: user.id,
                  organization_id: defaultOrg.id,
                  role: 'member',
                  created_at: new Date().toISOString()
                });
              
              // Recarregar para pegar a nova associação
              await fetchUserOrganizations();
              return;
            }
            
            setError('Erro ao criar organização. Entre em contato com o administrador.');
            setLoading(false);
            return;
          }
          
          // Adicionar usuário como admin da nova organização
          const { error: memberError } = await supabase
            .from('organization_members')
            .insert({
              user_id: user.id,
              organization_id: newOrg.id,
              role: 'admin',
              created_at: new Date().toISOString()
            });
          
          if (!memberError) {
            console.log('✅ Organização pessoal criada:', newOrg.name);
            // Recarregar para pegar a nova organização
            await fetchUserOrganizations();
            return;
          }
        } catch (err) {
          console.error('Erro ao criar organização pessoal:', err);
          setError('Erro ao configurar organização. Entre em contato com o administrador.');
        }
        
        setLoading(false);
        return;
      }

      console.log(`✅ Encontradas ${memberships.length} organização(ões) para o usuário`);
      
      // Extrair as organizações
      const userOrgs = memberships
        .filter(m => m.organization)
        .map(m => m.organization as Organization);
      
      setOrganizations(userOrgs);
      
      // Se já temos um organizationId no localStorage, usar ele
      const savedOrgId = localStorage.getItem('selectedOrganizationId');
      const savedOrgExists = savedOrgId && memberships.some(m => m.organization_id === savedOrgId);
      
      // Determinar qual organização usar
      let selectedOrgId: string;
      let selectedMembership: OrganizationMember;
      
      if (savedOrgExists) {
        // Usar a organização salva
        selectedOrgId = savedOrgId;
        selectedMembership = memberships.find(m => m.organization_id === savedOrgId)!;
        console.log('📌 Usando organização salva:', savedOrgId);
      } else {
        // Usar a primeira organização disponível
        selectedMembership = memberships[0];
        selectedOrgId = selectedMembership.organization_id;
        console.log('🏢 Usando primeira organização disponível:', selectedOrgId);
      }
      
      // Definir a organização atual
      setOrganizationIdState(selectedOrgId);
      setOrganization(selectedMembership.organization as Organization);
      setCurrentMembership(selectedMembership);
      
      // Salvar no localStorage
      localStorage.setItem('selectedOrganizationId', selectedOrgId);
      
      console.log('🎯 Organização selecionada:', {
        id: selectedOrgId,
        name: selectedMembership.organization?.name,
        role: selectedMembership.role
      });
      
    } catch (err) {
      console.error('❌ Erro ao carregar organizações:', err);
      setError('Erro ao carregar organizações. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Trocar de organização
  const switchOrganization = useCallback(async (newOrgId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Trocando para organização:', newOrgId);
      
      // Audit log - registrar tentativa de troca
      const auditEntry = {
        action: 'organization_switch_attempt',
        user_id: user?.id,
        from_org_id: organizationId,
        to_org_id: newOrgId,
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent
      };
      
      // Log local para desenvolvimento
      const auditLogs = JSON.parse(localStorage.getItem('org_audit_logs') || '[]');
      auditLogs.push(auditEntry);
      if (auditLogs.length > 50) auditLogs.shift(); // Manter últimos 50 logs
      localStorage.setItem('org_audit_logs', JSON.stringify(auditLogs));
      
      // Verificar se o usuário tem acesso a esta organização
      const { data: membership, error: membershipError } = await supabase
        .from('organization_members')
        .select(`
          id,
          user_id,
          organization_id,
          role,
          created_at,
          organization:organizations (
            id,
            name,
            created_at,
            updated_at
          )
        `)
        .eq('user_id', user?.id)
        .eq('organization_id', newOrgId)
        .single();

      if (membershipError) {
        console.error('❌ Erro ao verificar acesso:', membershipError);
        throw new Error('Você não tem acesso a esta organização');
      }

      if (!membership || !membership.organization) {
        throw new Error('Organização não encontrada');
      }

      // Atualizar estado
      setOrganizationIdState(newOrgId);
      setOrganization(membership.organization as Organization);
      setCurrentMembership(membership);
      
      // Salvar no localStorage
      localStorage.setItem('selectedOrganizationId', newOrgId);
      
      console.log('✅ Organização alterada com sucesso:', {
        id: newOrgId,
        name: membership.organization.name,
        role: membership.role
      });
      
      // Audit log - registrar sucesso
      const successEntry = {
        action: 'organization_switch_success',
        user_id: user?.id,
        from_org_id: organizationId,
        to_org_id: newOrgId,
        org_name: membership.organization.name,
        user_role: membership.role,
        timestamp: new Date().toISOString()
      };
      
      const logs = JSON.parse(localStorage.getItem('org_audit_logs') || '[]');
      logs.push(successEntry);
      if (logs.length > 50) logs.shift();
      localStorage.setItem('org_audit_logs', JSON.stringify(logs));
      
      // Recarregar a página para garantir que todos os componentes sejam atualizados
      // (opcional - pode ser removido se preferir atualização sem reload)
      window.location.reload();
      
    } catch (err: any) {
      console.error('❌ Erro ao trocar organização:', err);
      setError(err.message || 'Erro ao trocar de organização');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Função para definir organizationId (mantida para compatibilidade)
  const setOrganizationId = useCallback((id: string | null) => {
    if (id) {
      switchOrganization(id);
    }
  }, [switchOrganization]);

  // Atualizar organizações
  const refreshOrganizations = useCallback(async () => {
    await fetchUserOrganizations();
  }, [fetchUserOrganizations]);

  // Limpar erro
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Forçar refresh
  const forceRefresh = useCallback(() => {
    fetchUserOrganizations();
  }, [fetchUserOrganizations]);

  // Carregar organizações quando o usuário mudar
  useEffect(() => {
    if (user?.id) {
      fetchUserOrganizations();
    } else {
      // Limpar estado quando não há usuário
      setOrganizationIdState(null);
      setOrganization(null);
      setOrganizations([]);
      setCurrentMembership(null);
      setLoading(false);
    }
  }, [user?.id, fetchUserOrganizations]);

  // Listener para mudanças na tabela organization_members
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('organization-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'organization_members',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('🔔 Mudança detectada em organization_members:', payload);
          fetchUserOrganizations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, fetchUserOrganizations]);

  // 122 arquivos leem este contexto: o valor só muda quando algum campo muda.
  const value = useMemo(() => ({
    organizationId,
    organization,
    organizations,
    currentMembership,
    loading,
    error,
    setOrganizationId,
    switchOrganization,
    refreshOrganizations,
    clearError,
    forceRefresh
  }), [organizationId, organization, organizations, currentMembership, loading, error,
    setOrganizationId, switchOrganization, refreshOrganizations, clearError, forceRefresh]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};